// Weiterleitung schwalbennest-ruegen.de → passende .com-Seiten: vorbereitet, Schalter (nur Admin), Einbau-Blöcke zum Kopieren, Einbau-Prüfung
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ArrowRightLeft, Copy, Loader2, Search, RefreshCw, Image as ImageIcon } from "lucide-react";
import { Switch } from "./components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/com/redirect`;
const err = (e, f) => e.response?.data?.detail || f;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Berlin" }) : "–");
const btn = "flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 transition-colors disabled:opacity-50";
const KIND = { aktiv: ["leitet weiter (301)", "text-emerald-300"], bereit: ["PHP-Block bereit", "text-sky-300"], js: ["JS-Block bereit", "text-sky-300"],
  veraltet: ["alter Block (bitte neu einfügen)", "text-amber-300"],
  fehlt: ["noch kein Block", "text-eb-muted"], anders: ["leitet woandershin", "text-amber-300"], fehler: ["nicht erreichbar", "text-rose-300"] };

function CodeBox({ title, hint, code, testid }) {
  const [done, setDone] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(code); setDone(true); setTimeout(() => setDone(false), 2000); };
  return (
    <div className="bg-black/25 rounded-lg p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <b className="text-xs">{title}</b>
        <button className={btn} onClick={copy} data-testid={`${testid}-copy`}><Copy size={12} /> {done ? "Kopiert" : "Kopieren"}</button>
      </div>
      <p className="text-[11px] text-eb-muted">{hint}</p>
      <pre className="text-[10px] max-h-28 overflow-auto bg-black/40 rounded p-2 whitespace-pre-wrap break-all">{code}</pre>
    </div>
  );
}

function Go({ href, className = "", testid }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" title="Seite in neuem Tab öffnen" data-testid={testid}
      className={`truncate underline decoration-white/20 underline-offset-2 hover:decoration-current hover:text-eb-fg transition-colors ${className}`}>
      {href.replace("https://", "")}</a>
  );
}

// Vorschaubilder über den kostenlosen mShots-Dienst von WordPress.com (erstes Laden erzeugt das Bild, dann „Bilder neu laden“)
const shot = (url, v) => `https://s.wordpress.com/mshots/v1/${encodeURIComponent(url)}?w=480&h=300&v=${v}`;

function Thumb({ href, v, testid }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" title="Seite in neuem Tab öffnen" data-testid={testid}
      className="block mt-1.5 w-40 aspect-[16/10] rounded-md overflow-hidden border border-white/10 bg-black/30 hover:border-white/40 transition-colors">
      <img src={shot(href, v)} alt="" loading="lazy" className="w-full h-full object-cover object-top" />
    </a>
  );
}

function CheckResult({ c }) {
  const [pics, setPics] = useState(true);
  const [v, setV] = useState(0);
  return (
    <div className="mt-3" data-testid="redirect-check-result">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-[11px] text-eb-muted">Geprüft {fmt(c.at)} · {Object.entries(c.counts).filter(([, n]) => n).map(([k, n]) => `${n}× ${(KIND[k] || [k, ""])[0]}`).join(" · ")}</p>
        <button className={btn} onClick={() => setPics(!pics)} data-testid="redirect-thumbs-toggle"><ImageIcon size={12} /> {pics ? "Vorschaubilder aus" : "Vorschaubilder an"}</button>
        {pics && <button className={btn} onClick={() => setV(v + 1)} data-testid="redirect-thumbs-reload"><RefreshCw size={12} /> Bilder neu laden</button>}
      </div>
      <div className={`mt-2 divide-y divide-white/5 text-xs overflow-auto ${pics ? "max-h-[560px]" : "max-h-64"}`}>
        {c.rows.map((r) => (
          <div key={r.from} className="py-2 grid sm:grid-cols-[1fr_1fr_150px] gap-2" data-testid={`redirect-row-${r.from}`}>
            <div className="min-w-0"><Go href={r.from} className="text-eb-muted" testid={`redirect-row-from-${r.from}`} />
              {pics && <Thumb href={r.from} v={v} testid={`redirect-thumb-de-${r.from}`} />}</div>
            <div className="min-w-0"><span className="truncate">→ <Go href={r.to} testid={`redirect-row-to-${r.from}`} /></span>
              {pics && <Thumb href={r.to} v={v} testid={`redirect-thumb-com-${r.from}`} />}</div>
            <span className={`font-semibold ${(KIND[r.kind] || [r.kind, "text-eb-muted"])[1]}`} title={r.detail}>{(KIND[r.kind] || [r.kind, "text-eb-muted"])[0]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RedirectCard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => axios.get(API).then((r) => setD(r.data)).catch((e) => setMsg(err(e, "Weiterleitung nicht ladbar"))), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (v) => {
    const q = v ? "Ab sofort werden alle Besucher der schwalbennest-ruegen.de dauerhaft (301) auf die passenden .com-Seiten geschickt. Wirklich einschalten?"
      : "Weiterleitung ausschalten? Die .de zeigt dann wieder ihre eigenen Seiten.";
    if (!window.confirm(q)) return;
    setBusy("toggle"); setMsg("");
    try { setD((await axios.put(API, { active: v })).data); } catch (e) { setMsg(err(e, "Schalten fehlgeschlagen")); }
    setBusy("");
  };
  const check = async () => {
    setBusy("check"); setMsg("");
    try { const r = await axios.post(`${API}/check`); setD({ ...d, check: r.data }); } catch (e) { setMsg(err(e, "Prüfung fehlgeschlagen")); }
    setBusy("");
  };
  if (!d) return msg ? <p className="text-sm text-rose-400">{msg}</p> : null;
  return (
    <div className="bg-eb-surface border border-white/5 rounded-xl p-5" data-testid="redirect-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><ArrowRightLeft size={16} /> Weiterleitung .de → .com</h3>
          <p className="text-xs text-eb-muted mt-1">Jede .de-Seite führt auf dieselbe Seite der .com (alte Herbstfest-Links inkl. Buchungsdaten). Angemeldete WordPress-Nutzer bleiben auf der .de.</p>
        </div>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${d.active ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-eb-muted"}`} data-testid="redirect-status">
          {d.active ? `aktiv seit ${fmt(d.updated_at)}` : "aus · vorbereitet"}</span>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <Switch checked={d.active} disabled={!!busy} onCheckedChange={toggle} data-testid="redirect-switch"
          className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-white/20 border-white/10 [&>span]:bg-white" />
        <span className="text-sm">{d.active ? "Besucher der .de landen auf der .com" : "Ausgeschaltet – erst einschalten, wenn die .com die Hauptseite sein soll"}</span>
      </div>
      {d.updated_by && <p className="text-[11px] text-eb-muted mt-1" data-testid="redirect-updated">Zuletzt geschaltet {fmt(d.updated_at)} von {d.updated_by}</p>}
      <details className="mt-3 text-xs" data-testid="redirect-map">
        <summary className="cursor-pointer text-eb-muted">{d.map.length} Adressen ansehen</summary>
        <div className="mt-2 grid gap-1">{d.map.map((m) => <div key={m.from} className="truncate"><Go href={m.from} className="text-eb-muted" testid={`redirect-map-from-${m.from}`} /> → <Go href={m.to} testid={`redirect-map-to-${m.from}`} /></div>)}</div>
      </details>
      <div className="grid lg:grid-cols-2 gap-3 mt-4">
        <CodeBox testid="redirect-php" title="A · Mit WordPress-Admin der .de (empfohlen)" code={d.php}
          hint="Echte 301-Weiterleitung für Google. Design → Theme-Datei-Editor → Divi-Child → functions.php ganz ans Ende einfügen (oder Plugin „Code Snippets“). Nach dem Einschalten den Seiten-Cache der .de leeren." />
        <CodeBox testid="redirect-js" title="B · Ohne Admin: JS-Block" code={d.js}
          hint="In ein Code-Modul der .de-Seite einfügen. Auf der Herbstfest-Seite steckt er schon im Ladeblock. Google erkennt diese Variante schwächer als A." />
      </div>
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <button className={btn} disabled={!!busy} onClick={check} data-testid="redirect-check">
          {busy === "check" ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Einbau auf der .de prüfen</button>
      </div>
      {d.check && <CheckResult c={d.check} />}
      {msg && <p className="text-xs text-rose-400 mt-2" data-testid="redirect-msg">{msg}</p>}
    </div>
  );
}
